import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiGetBlob, apiPost } from "../api/http";
import { downloadCsv } from "../utils/csv";
import { parseApiError } from "../utils/apiErrors";
import { useToast } from "../context/ToastContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import DataTable from "../components/shared/DataTable";
import { formatDateOnly } from "../utils/dateTime";
import { FileText, BarChart3, ChevronLeft, ChevronRight, Download, ExternalLink } from "lucide-react";

function buildExportFilename(prefix) {
  const now = new Date();
  const ts = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `${prefix}-${ts}.csv`;
}

const PAGE_SIZE = 20;

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function truncate(text, max = 72) {
  if (text == null || text === "") return "--";
  const s = String(text).replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}...`;
}

function h2Count(h2) {
  if (h2 == null || h2 === "") return 0;
  if (Array.isArray(h2)) return h2.length;
  try {
    const p = JSON.parse(h2);
    return Array.isArray(p) ? p.length : 0;
  } catch {
    return 0;
  }
}

function imageCount(images) {
  if (images == null || images === "") return 0;
  try {
    const p = typeof images === "string" ? JSON.parse(images) : images;
    if (p && typeof p === "object" && !Array.isArray(p)) return p.total || 0;
    return Array.isArray(p) ? p.length : 0;
  } catch {
    return 0;
  }
}

function scoreColor(score) {
  if (score == null) return "var(--text-muted)";
  if (score >= 80) return "var(--success)";
  if (score >= 60) return "#84cc16";
  if (score >= 40) return "var(--warning)";
  return "var(--danger)";
}

function extractDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const seoCsvColumns = [
  { key: "url", label: "URL" },
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  { key: "h1", label: "H1" },
  { key: "seoScore", label: "SEO Score" },
  { key: "h2Count", label: "H2 Count" },
  { key: "imageCount", label: "Image Count" },
  { key: "createdAt", label: "Created At" },
];

const deepCsvColumns = [
  { key: "url", label: "URL" },
  { key: "score", label: "Lighthouse Score" },
  { key: "pdfStatus", label: "PDF Status" },
  { key: "createdAt", label: "Created At" },
];

// ─── SEO Audits Tab ───────────────────────────────────────────────────────────

const SeoAuditsTab = ({ activeSite }) => {
  const { showError } = useToast();
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [healthFilter, setHealthFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    const endpoint = activeSite ? "/api/seo/audit-history/domain" : "/api/seo/audit-history";
    const params = activeSite
      ? {
          domain: extractDomain(activeSite.url),
          page,
          limit: PAGE_SIZE,
          search: searchQuery || undefined,
          scoreFilter: scoreFilter !== "all" ? scoreFilter : undefined,
          healthFilter: healthFilter !== "all" ? healthFilter : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          sortBy: sortBy || undefined,
        }
      : {
          page,
          limit: PAGE_SIZE,
          search: searchQuery || undefined,
          scoreFilter: scoreFilter !== "all" ? scoreFilter : undefined,
          healthFilter: healthFilter !== "all" ? healthFilter : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          sortBy: sortBy || undefined,
        };

    apiGet(endpoint, params)
      .then(({ data }) => {
        setHistory(data.data || data);
        setPagination(data.pagination || null);
      })
      .catch((err) => showError(parseApiError(err, "Failed to load SEO audit history")))
      .finally(() => setLoading(false));
  }, [page, activeSite, showError, searchQuery, scoreFilter, dateFrom, dateTo, healthFilter, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [activeSite, searchQuery, scoreFilter, dateFrom, dateTo, healthFilter, sortBy]);

  const filteredHistory = useMemo(() => history, [history]);

  const exportRows = useMemo(
    () =>
      filteredHistory.map((audit) => ({
        url: audit.url,
        title: audit.title,
        description: audit.description,
        h1: audit.h1,
        seoScore: audit.seo_score ?? "",
        h2Count: h2Count(audit.h2),
        imageCount: imageCount(audit.images),
        createdAt: new Date(audit.created_at).toISOString(),
      })),
    [filteredHistory]
  );

  return (
    <>
      <div className="filter-group" style={{ marginBottom: 20 }}>
        <div className="filter-item search">
          <label className="filter-label">Search</label>
          <input
            type="text"
            placeholder="URL or Page Title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input"
            style={{ width: "100%", minWidth: 0 }}
          />
        </div>
        <div className="filter-item">
          <label className="filter-label">Score</label>
          <select value={scoreFilter} onChange={(e) => setScoreFilter(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }}>
            <option value="all">All Scores</option>
            <option value="excellent">80-100</option>
            <option value="good">60-79</option>
            <option value="fair">40-59</option>
            <option value="poor">Below 40</option>
          </select>
        </div>
        <div className="filter-item">
          <label className="filter-label">Status</label>
          <select value={healthFilter} onChange={(e) => setHealthFilter(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }}>
            <option value="all">All Status</option>
            <option value="healthy">Healthy</option>
            <option value="needs-attention">Needs Attention</option>
          </select>
        </div>
        <div className="filter-item">
          <label className="filter-label">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }} />
        </div>
        <div className="filter-item">
          <label className="filter-label">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }} />
        </div>
        <div className="filter-item">
          <label className="filter-label">Sort</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="highest-score">Highest Score</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="card skeleton-table">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="skeleton-block skeleton-row" style={{ height: "60px", marginBottom: "12px" }} />
          ))}
        </div>
      ) : filteredHistory.length > 0 ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px", marginBottom: "16px" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "var(--text-heading)" }}>SEO Page Audits</h3>
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "2px", display: "block" }}>{filteredHistory.length} record{filteredHistory.length !== 1 ? "s" : ""}</span>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => downloadCsv(buildExportFilename("seo-audit-history"), seoCsvColumns, exportRows)}
              disabled={exportRows.length === 0}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
          <DataTable
            headers={[
              {
                key: "url",
                label: "TARGET PAGE",
                render: (audit) => (
                  <>
                    <a href={audit.url} target="_blank" rel="noreferrer" className="audit-link" style={{ fontWeight: 600 }}>
                      {truncate(audit.url, 40)}
                    </a>
                    <div className="audit-subtext">{truncate(audit.title, 40)}</div>
                  </>
                ),
              },
              {
                key: "seo_score",
                label: "SEO SCORE",
                render: (audit) => (
                  <span style={{ fontWeight: 700, color: scoreColor(audit.seo_score) }}>
                    {audit.seo_score ?? "—"}
                  </span>
                ),
              },
              {
                key: "structure",
                label: "STRUCTURE (H1/H2)",
                render: (audit) => (
                  <>
                    <div>H1: {truncate(audit.h1, 20)}</div>
                    <div className="audit-subtext">{h2Count(audit.h2)} H2 tags</div>
                  </>
                ),
              },
              {
                key: "images",
                label: "ASSETS",
                render: (audit) => (
                  <>
                    <div className="asset-count" style={{ fontWeight: 600 }}>{imageCount(audit.images)}</div>
                    <div className="audit-subtext">Images</div>
                  </>
                ),
              },
              {
                key: "created_at",
                label: "DATE",
                render: (audit) => formatDateOnly(audit.created_at),
              },
              {
                key: "status",
                label: "STATUS",
                render: (audit) => (
                  <span className={`status-pill ${(audit.seo_score ?? 0) >= 60 ? "healthy" : "action"}`}>
                    {(audit.seo_score ?? 0) >= 60 ? "Healthy" : "Action Required"}
                  </span>
                ),
              },
              {
                key: "actions",
                label: "",
                render: (audit) => (
                  <Link to={`/report/${audit.share_token}`} className="btn btn-sm btn-ghost">
                    View Report
                  </Link>
                ),
              },
            ]}
            data={filteredHistory}
            keyExtractor={(audit) => audit.id}
          />
          {pagination && pagination.totalPages > 1 ? (
            <div className="pagination" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
              >
                <ChevronLeft size={15} /> Prev
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} &mdash; {pagination.total} total
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
              >
                Next <ChevronRight size={15} />
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="card empty-state" style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)" }}>
          No SEO audits found.
        </div>
      )}
    </>
  );
};

// ─── Deep Audits Tab ──────────────────────────────────────────────────────────

const DeepAuditsTab = ({ activeSite }) => {
  const { showError, showInfo, showSuccess } = useToast();
  const [audits, setAudits] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [viewingId, setViewingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [retryingIds, setRetryingIds] = useState(new Set());
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    setLoading(true);
    apiGet("/api/audit/audits", {
      page,
      limit: PAGE_SIZE,
      search: searchQuery || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      sortBy: sortBy || undefined,
    })
      .then(({ data }) => {
        let rows = data?.data || data || [];
        // Filter by active site if selected
        if (activeSite) {
          try {
            const activeHost = new URL(activeSite.url).hostname.replace(/^www\./i, "").toLowerCase();
            rows = rows.filter((r) => {
              try {
                const rowHost = new URL(r.website_url).hostname.replace(/^www\./i, "").toLowerCase();
                return rowHost === activeHost;
              } catch {
                return false;
              }
            });
          } catch {
            // ignore domain parse error
          }
        }
        setAudits(rows);
        setPagination(data?.pagination || null);
      })
      .catch((err) => showError(parseApiError(err, "Failed to load deep audit history")))
      .finally(() => setLoading(false));
  }, [page, activeSite, showError, searchQuery, dateFrom, dateTo, sortBy, refreshTrigger]);

  useEffect(() => {
    setPage(1);
  }, [activeSite, searchQuery, dateFrom, dateTo, sortBy]);

  const exportRows = useMemo(
    () =>
      audits.map((a) => ({
        url: a.website_url,
        score: a.score ?? "",
        pdfStatus: a.pdf_ready ? "Ready" : (a.audit_data?.pdfStatus === "failed" ? "Failed" : "Pending"),
        createdAt: new Date(a.created_at).toISOString(),
      })),
    [audits]
  );

  const handleViewPdf = async (audit) => {
    // Always go through the authenticated backend API — never expose
    // the internal storage reference (r2:... / puter:...) to the browser.
    // ?inline=1 sets Content-Disposition: inline so the browser renders
    // the PDF in a new tab instead of triggering a download.
    setViewingId(audit.id);
    try {
      const response = await apiGetBlob(`/api/audit/reports/${audit.id}?inline=1`);
      const file = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(file);
      const tab = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!tab) {
        // Popup blocked fallback — trigger download instead
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `deep-audit-${audit.id}.pdf`;
        link.click();
      }
      // Revoke the blob URL after a delay to free memory once browser has loaded it
      setTimeout(() => URL.revokeObjectURL(blobUrl), 15000);
    } catch (err) {
      showError(parseApiError(err, "Failed to open PDF report"));
    } finally {
      setViewingId(null);
    }
  };

  const handleDownloadPdf = async (audit) => {
    setDownloadingId(audit.id);
    try {
      const response = await apiGetBlob(`/api/audit/reports/${audit.id}`);
      downloadBlob(response.data, `deep-audit-${audit.id}.pdf`);
      showInfo("PDF report download started.");
    } catch (err) {
      showError(parseApiError(err, "Failed to download PDF report"));
    } finally {
      setDownloadingId(null);
    }
  };

  const triggerRetryPdf = async (auditId) => {
    setRetryingIds((prev) => {
      const next = new Set(prev);
      next.add(auditId);
      return next;
    });
    try {
      await apiPost(`/api/audit/${auditId}/retry-pdf`);
      showSuccess("PDF generation re-triggered!");
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      showError(parseApiError(err, "Failed to re-trigger PDF generation"));
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(auditId);
        return next;
      });
    }
  };

  const pdfStatusBadge = (audit) => {
    const pdfStatus = audit.audit_data?.pdfStatus;
    if (audit.pdf_ready) {
      return <span className="status-pill healthy">Ready</span>;
    }
    if (pdfStatus === "failed") {
      return <span className="status-pill action">Failed</span>;
    }
    return <span style={{ fontSize: "0.8rem", color: "var(--warning)", fontWeight: 500 }}>Generating…</span>;
  };

  return (
    <>
      <div className="filter-group" style={{ marginBottom: 20 }}>
        <div className="filter-item search">
          <label className="filter-label">Search URL</label>
          <input
            type="text"
            placeholder="Domain or URL..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input"
            style={{ width: "100%", minWidth: 0 }}
          />
        </div>
        <div className="filter-item">
          <label className="filter-label">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }} />
        </div>
        <div className="filter-item">
          <label className="filter-label">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }} />
        </div>
        <div className="filter-item">
          <label className="filter-label">Sort</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="input" style={{ width: "100%", minWidth: 0 }}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="highest-score">Highest Score</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="card skeleton-table">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="skeleton-block skeleton-row" style={{ height: "60px", marginBottom: "12px" }} />
          ))}
        </div>
      ) : audits.length > 0 ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px", marginBottom: "16px" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "var(--text-heading)" }}>Deep Lighthouse Audits</h3>
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "2px", display: "block" }}>{audits.length} record{audits.length !== 1 ? "s" : ""}</span>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => downloadCsv(buildExportFilename("deep-audit-history"), deepCsvColumns, exportRows)}
              disabled={exportRows.length === 0}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
          <DataTable
            headers={[
              {
                key: "website_url",
                label: "AUDITED URL",
                render: (a) => (
                  <a href={a.website_url} target="_blank" rel="noreferrer" className="audit-link" style={{ fontWeight: 600 }}>
                    {truncate(a.website_url, 45)}
                  </a>
                ),
              },
              {
                key: "score",
                label: "LIGHTHOUSE SCORE",
                render: (a) => (
                  <span style={{ fontWeight: 700, color: scoreColor(a.score), fontSize: "1rem" }}>
                    {a.score != null ? `${a.score}/100` : "—"}
                  </span>
                ),
              },
              {
                key: "pdf_status",
                label: "PDF REPORT",
                render: (a) => pdfStatusBadge(a),
              },
              {
                key: "created_at",
                label: "DATE",
                render: (a) => formatDateOnly(a.created_at),
              },
              {
                key: "actions",
                label: "",
                render: (a) => {
                  const pdfReady = !!a.pdf_ready;
                  const pdfFailed = a.audit_data?.pdfStatus === "failed";
                  const isRetrying = retryingIds.has(a.id);
                  if (pdfFailed) {
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "0.8rem", color: "var(--danger)" }}>PDF failed</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          disabled={isRetrying}
                          onClick={() => triggerRetryPdf(a.id)}
                          style={{ padding: "4px 10px", fontSize: "0.8rem", color: "var(--accent)" }}
                        >
                          {isRetrying ? "Queuing…" : "Regenerate"}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={!pdfReady || viewingId === a.id}
                        onClick={() => handleViewPdf(a)}
                        style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                      >
                        {viewingId === a.id ? "Opening…" : "View"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={!pdfReady || downloadingId === a.id}
                        onClick={() => handleDownloadPdf(a)}
                        style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                      >
                        {downloadingId === a.id ? "Downloading…" : "Download"}
                      </button>
                    </div>
                  );
                },
              },
            ]}
            data={audits}
            keyExtractor={(a) => a.id}
          />
          {pagination && pagination.totalPages > 1 ? (
            <div className="pagination" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
              >
                <ChevronLeft size={15} /> Prev
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} &mdash; {pagination.total} total
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
              >
                Next <ChevronRight size={15} />
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="card empty-state" style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)" }}>
          No deep audits found.{" "}
          <Link to="/deep-audit" style={{ fontWeight: 600 }}>
            Run your first deep audit →
          </Link>
        </div>
      )}
    </>
  );
};

// ─── Main AuditHistory Page ───────────────────────────────────────────────────

const AuditHistory = () => {
  const { activeSite } = useActiveSite();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read active tab from URL: ?tab=deep defaults to seo
  const activeTab = searchParams.get("tab") === "deep" ? "deep" : "seo";

  const setTab = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div className="page page-admin-wide">
      {/* Page Header */}
      <div style={{ marginBottom: "28px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h1 className="page-title" style={{ marginBottom: "6px" }}>Audit History</h1>
            <p className="dashboard-lead" style={{ margin: 0 }}>
              All your SEO audits and deep Lighthouse reports in one place.
            </p>
          </div>
          <Link
            to="/domain-history"
            className="btn btn-ghost btn-sm"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", alignSelf: "flex-start", marginTop: "4px" }}
          >
            <ExternalLink size={14} /> Domain View
          </Link>
        </div>
      </div>

      {/* Tab Bar — full-width segmented control */}
      <div
        style={{
          display: "flex",
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          padding: "4px",
          gap: "4px",
          marginBottom: "24px",
          boxShadow: "var(--shadow)",
        }}
      >
        <button
          type="button"
          className={`dashboard-tab-btn ${activeTab === "seo" ? "active" : ""}`}
          style={{
            flex: 1,
            borderRadius: "9px",
            padding: "9px 16px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "7px",
            fontSize: "0.9rem",
          }}
          onClick={() => setTab("seo")}
        >
          <BarChart3 size={15} /> SEO Audits
        </button>
        <button
          type="button"
          className={`dashboard-tab-btn ${activeTab === "deep" ? "active" : ""}`}
          style={{
            flex: 1,
            borderRadius: "9px",
            padding: "9px 16px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "7px",
            fontSize: "0.9rem",
          }}
          onClick={() => setTab("deep")}
        >
          <FileText size={15} /> Deep Audits
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "seo" ? (
        <SeoAuditsTab activeSite={activeSite} />
      ) : (
        <DeepAuditsTab activeSite={activeSite} />
      )}
    </div>
  );
};

export default AuditHistory;
